import { useMemo, useState, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import Card from "components/card";
import WaterMetersPicker, { MeterOption } from "./WaterMetersPicker";
// import PieChart from "components/charts/PieChart"; // removed (tags chart gone)
import DonutChart from "components/charts/DonutChart";
import SimpleTable from "components/tables/SimpleTable";
import CustomButton from "components/button";
import { Link, useParams } from "react-router-dom";
import { reqFunction } from "utils/API";
import ProjectConsumptionChart from "./ProjectConsumptionChart";
import { ChartOptions } from "views/systemReports/ProjecsReport";
import { MdRemoveRedEye } from "react-icons/md";
import { DeviceObj } from "views/counters";
import { ProjectObject } from "views/projects";
import { counterTagSelect } from "variables";
import { renderTimeJalali } from "utils/TimeUtiles";
import { ApexOptions } from "apexcharts";
import { FiMapPin } from "react-icons/fi";
import { IoIosWater } from "react-icons/io";
import { MdCalendarMonth, MdLeaderboard } from "react-icons/md";
import { FaRegUser } from "react-icons/fa";
import { IoWaterOutline } from "react-icons/io5";
import { FaFaucetDrip } from "react-icons/fa6";
import meterPng from "assets/img/avatars/water-meter.png";
import { MdHistory, MdAccessTime } from "react-icons/md";

// ---------- Types ----------
interface DashboardSummary {
  project: {
    id: string;
    name: string;
    location: string;
    employer: { name?: string; lastName?: string; phoneNumber?: string };
    total_devices: number;
    types: { name: string; count: number }[];
    tags: { id: string; name: string; count: number }[];
  };
  consumption: {
    total_all_devices: number;
    top_month: { serial: string; name: string; value: number } | null;
    top_all_time: { serial: string; name: string; value: number } | null;
  };
  outdated_devices: {
    serial: string;
    name: string;
    last_time: string | null;
    delay_days: number | null;
  }[];
  recent_5_consumptions: {
    serial: string;
    name: string;
    create_time: string;
    value: number;
    cumulative_value: number;
  }[];
}

interface ProjectAbstractProps {
  projectData: [ProjectObject];
  projectDevices: DeviceObj[];
  updateInfo: () => void;
}

// ---------- Helpers ----------
const formatAsCubicMeter = (val?: number | null) => {
  if (val === null || val === undefined) return "__";
  return (val / 1000).toLocaleString(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
};

const delayBadge = (days?: number | null) => {
  if (days === null || days === undefined || Number.isNaN(days)) {
    return (
      <span className="rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">
        __
      </span>
    );
  }
  const cls =
    days >= 4
      ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200"
      : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-200";
  return (
    <span className={`rounded px-2 py-0.5 text-xs ${cls}`}>{days} روز</span>
  );
};

// put this next to your other helpers (below delayBadge, etc.)
const valueBadge = (content?: ReactNode) => {
  const isEmpty =
    content === null ||
    content === undefined ||
    (typeof content === "string" && content === "__");

  return (
    <span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900/40 dark:text-green-200">
      {isEmpty ? "__" : content}
    </span>
  );
};

const TopLoadingBar = ({ show }: { show: boolean }) => {
  if (!show) return null;
  return (
    <div className="bg-transparent fixed left-0 top-0 z-[9999] h-0.5 w-full">
      <div className="h-full w-1/3 animate-[loading_1.2s_infinite_linear] bg-blue-500" />
      <style>{`
        @keyframes loading {
          0% { margin-left: -33%; width: 15%; }
          50% { margin-left: 45%; width: 40%; }
          100% { margin-left: 100%; width: 10%; }
        }
      `}</style>
    </div>
  );
};

const BlockSkeleton = ({ height = 140 }: { height?: number }) => (
  <div
    className="w-full rounded-xl bg-gray-200 dark:bg-gray-700"
    style={{ height }}
  />
);

const ProjectAbstract = ({ projectData }: ProjectAbstractProps) => {
  const info = projectData?.[0];
  const { projectId } = useParams();
  const WATER_TAG_ID = counterTagSelect[0]?.value;

  // paged-in-box limits
  const [outdatedLimit, setOutdatedLimit] = useState(5);
  const [recentLimit, setRecentLimit] = useState(5);

  const [metersOpen, setMetersOpen] = useState(false);
  const [selectedMeters, setSelectedMeters] = useState<string[]>([]);

  // fetch meters for this project (only when picker open)
  const { data: metersData } = useQuery({
    enabled: metersOpen,
    queryFn: () =>
      reqFunction(
        "watermeters/admin/getAll",
        {
          page: 1,
          count: 1000,
          user_id: null,
          project_id: projectId,
          water_meter_serial: null,
          water_meter_tag_id: null,
        },
        "post"
      ),
    queryKey: ["meters", "byProject", projectId],
  });

  const meterOptions: MeterOption[] = useMemo(() => {
    const arr = metersData?.data ?? [];
    return arr.map((m: any) => ({
      value: m.water_meter_serial,
      label: m.water_meter_name || m.water_meter_serial,
      meta: m,
    }));
  }, [metersData]);

  const {
    data: summary,
    isLoading,
    isFetching,
    status,
    refetch,
  } = useQuery({
    queryFn: () =>
      reqFunction(
        "watermeters/v2/admin/dashboard/summary",
        {
          // if meters chosen → use them; otherwise use project
          water_meters: selectedMeters.length ? selectedMeters : null,
          project_id: selectedMeters.length ? null : projectId,
          outdated_tag_id: WATER_TAG_ID,
        },
        "post"
      ),
    queryKey: [
      "dashboardSummary",
      projectId as string,
      WATER_TAG_ID as string,
      JSON.stringify([...selectedMeters].sort()), // key changes when selection changes
    ],
    select: (res: any): DashboardSummary => res.data as DashboardSummary,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev as any,
  });

  const loading = isLoading || status !== "success";

  // Map item types
  type TypeItem = DashboardSummary["project"]["types"][number];
  type OutdatedItem = DashboardSummary["outdated_devices"][number];
  type RecentItem = DashboardSummary["recent_5_consumptions"][number];

  // انواع دستگاه donut options (legend on top so chart gets bigger underneath)
  const typeChartOptions: ApexOptions = useMemo(
    () => ({
      labels: summary?.project.types.map((t) => t.name) ?? [],
      legend: {
        position: "top",
        horizontalAlign: "left",
        floating: false,
        fontSize: "12px",
        markers: { radius: 6 },
        itemMargin: { vertical: 4, horizontal: 8 },
      },
      chart: { parentHeightOffset: 0 },
      plotOptions: { pie: { donut: { size: "60%" } } },
      dataLabels: {
        enabled: true,
        formatter: (v: number) => `${v.toFixed(1)}%`,
      },
      tooltip: { y: { formatter: (v: number) => v.toLocaleString() } },
    }),
    [summary]
  );
  const typeChartData = useMemo<number[]>(
    () => summary?.project.types.map((t: TypeItem) => t.count) ?? [],
    [summary]
  );

  // Right column lists (data + slicing)
  const outdatedHeader = [
    { title: "نام دستگاه", headerKey: "name" },
    { title: "سریال", headerKey: "serial" },
    { title: "آخرین ارسال", headerKey: "last" },
    { title: "تاخیر (روز)", headerKey: "delay" },
    { title: "مشاهده", headerKey: "actions" },
  ];
  const outdatedFull =
    summary?.outdated_devices?.map((d: OutdatedItem) => ({
      name: <span className="truncate">{d.name}</span>,
      serial: d.serial,
      last: (
        <span>
          {d.last_time ? renderTimeJalali(d.last_time, "chart") : "__"}
        </span>
      ),
      delay: delayBadge(d.delay_days), // ⬅️ چیپ رنگی
      actions: (
        <div className="flex items-center justify-center">
          <Link to={`/admin/counters/${d.serial}`} rel="noopener noreferrer">
            <CustomButton
              text=""
              icon={<MdRemoveRedEye />}
              color="blue"
              extra="!p-2"
              onClick={() => {}}
            />
          </Link>
        </div>
      ),
    })) || [];
  const outdatedData = outdatedFull.slice(0, outdatedLimit);

  const recentHeader = [
    { title: "نام دستگاه", headerKey: "name" },
    { title: "سریال", headerKey: "serial" },
    { title: "زمان ثبت", headerKey: "time" },
    { title: "مقدار", headerKey: "val" },
    { title: "تجمیعی", headerKey: "cum" },
  ];
  const recentFull =
    summary?.recent_5_consumptions?.map((r: RecentItem) => ({
      name: <span className="truncate">{r.name}</span>,
      serial: r.serial,

      // green badge for time (JSX is fine now)
      time: valueBadge(renderTimeJalali(r.create_time, "chart")),

      // green badge for val (number then unit on the RIGHT)
      val: valueBadge(
        <span className="inline-flex items-baseline gap-1" dir="ltr">
          <span>{formatAsCubicMeter(r.value)}</span>
          <span>m³</span>
        </span>
      ),

      // cum stays as non-badge with correct order & alignment
      cum: (
        <span className="flex items-baseline justify-end gap-1" dir="ltr">
          <span>{formatAsCubicMeter(r.cumulative_value)}</span>
          <span>m³</span>
        </span>
      ),
    })) || [];

  const recentData = recentFull.slice(0, recentLimit);

  // skeleton rows
  const sk = (cols: string[]) => {
    const cell = (
      <div className="h-4 w-3/4 animate-pulse rounded bg-gray-300 dark:bg-gray-700" />
    );
    const obj: any = {};
    cols.forEach((k) => {
      obj[k] =
        k === "actions" ? (
          <div className="mx-auto h-6 w-6 animate-pulse rounded bg-gray-300 dark:bg-gray-700" />
        ) : (
          cell
        );
    });
    return obj;
  };

  const canShowMoreOutdated = outdatedLimit < outdatedFull.length;
  const canShowMoreRecent = recentLimit < recentFull.length;

  return (
    <div className="dashboard-compact min-w-0 pt-1">
      {/* compact inner chart height & improve tables on small screens */}
      <style>{`
        .dashboard-compact .chart-container { height: 220px !important; }
        @media (min-width: 640px) { .dashboard-compact .chart-container { height: 260px !important; } }
        @media (min-width: 1024px) { .dashboard-compact .chart-container { height: 280px !important; } }
      `}</style>

      {/* one top loader + refresh */}
      <TopLoadingBar show={isLoading || isFetching} />

      <Card extra="p-3">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="truncate text-base font-bold text-navy-700 dark:text-white">
            {info?.water_meter_project_name || summary?.project.name || "پروژه"}
          </div>
          <div className="flex items-center gap-2">
            <CustomButton
              text="انتخاب کنتورها"
              onClick={() => setMetersOpen(true)}
              color="blue"
              extra="!h-8 !px-3"
            />
            <CustomButton
              text={isFetching ? "در حال بازآوری..." : "بازآوری"}
              onClick={() => refetch()}
              color={isFetching ? "gray" : "blue"}
              isDisabled={isFetching}
              extra="!h-8 !px-3 flex items-center gap-2"
              icon={
                isFetching ? (
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="3"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    ></path>
                  </svg>
                ) : undefined
              }
            />
          </div>
        </div>

        {/* right under the toolbar (optional helper line) */}
        <div className="mb-2 text-[12px] text-gray-600 dark:text-gray-300">
          {selectedMeters.length
            ? `${selectedMeters.length} کنتور انتخاب شده — داشبورد بر اساس این کنتورهاست`
            : "هیچ کنتوری انتخاب نشده؛ داشبورد بر اساس همه دستگاه‌های پروژه است."}
        </div>

        {/* main grid: 7 columns on lg, left=3, right=4; stacks nicely on small */}
        <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-7">
          {/* LEFT (col-span-3 on lg) */}
          <div className="col-span-1 flex min-w-0 flex-col gap-3 md:col-span-2 lg:col-span-3">
            {/* project info */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {/* نام پروژه */}
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 shadow-sm dark:border-gray-700">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
                  <IoWaterOutline className="h-5 w-5 text-[#362f2f]" />
                </div>
                <div>
                  <div className="mb-1 text-xs text-gray-500">نام پروژه</div>
                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {loading ? (
                      <div className="h-4 w-24 animate-pulse rounded bg-gray-300 dark:bg-gray-700" />
                    ) : (
                      summary?.project?.name || "__"
                    )}
                  </div>
                </div>
              </div>

              {/* محل انجام پروژه */}
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 shadow-sm dark:border-gray-700">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
                  <FiMapPin className="h-5 w-5 text-[#362f2f]" />
                </div>
                <div>
                  <div className="mb-1 text-xs text-gray-500">
                    محل انجام پروژه
                  </div>
                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {loading ? (
                      <div className="h-4 w-28 animate-pulse rounded bg-gray-300 dark:bg-gray-700" />
                    ) : (
                      summary?.project?.location || "__"
                    )}
                  </div>
                </div>
              </div>

              {/* نام کارفرما */}
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 shadow-sm dark:border-gray-700">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
                  <FaRegUser className="h-4 w-4 text-[#362f2f]" />
                </div>
                <div>
                  <div className="mb-1 text-xs text-gray-500">نام کارفرما</div>
                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {loading ? (
                      <div className="h-4 w-28 animate-pulse rounded bg-gray-300 dark:bg-gray-700" />
                    ) : summary?.project?.employer ? (
                      `${summary.project.employer.name} ${summary.project.employer.lastName}`
                    ) : (
                      "__"
                    )}
                  </div>
                </div>
              </div>

              {/* تعداد کل دستگاه‌ها */}
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 shadow-sm dark:border-gray-700">
                {/* icon container (keeps same size/shape as before) */}
                <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
                  <img
                    src={meterPng} // or src="/water-meter.png" if in public/
                    alt="Water meter"
                    className="h-6 w-6 object-contain sm:h-7 sm:w-7"
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                <div>
                  <div className="mb-1 text-xs text-gray-500">
                    تعداد کل دستگاه‌ها
                  </div>
                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {loading ? (
                      <div className="h-4 w-16 animate-pulse rounded bg-gray-300 dark:bg-gray-700" />
                    ) : (
                      `${summary?.project?.total_devices ?? "__"} عدد`
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* انواع دستگاه + KPIs side-by-side (aligned) */}
            <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-3">
              {/* انواع دستگاه (gets 2/3 width, grows tall) */}
              <div className="flex h-full flex-col rounded-xl border p-2 sm:col-span-2">
                <div className="mb-1 text-[11px] text-gray-500">
                  انواع دستگاه
                </div>
                {/* bigger donut under the options */}
                <div className="min-h-[220px] flex-1 sm:min-h-[260px] lg:min-h-[320px]">
                  {loading ? (
                    <BlockSkeleton height={320} />
                  ) : (
                    <DonutChart
                      chartOptions={typeChartOptions}
                      chartData={typeChartData}
                    />
                  )}
                </div>
              </div>

              {/* KPI Column — perfectly aligned: takes 1/3 width and fills height */}
              <div className="flex h-full flex-col gap-3 sm:col-span-1">
                {/* مصرف کل */}
                <div className="flex flex-1 flex-col justify-between rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 p-3 shadow-sm transition-all duration-200 hover:shadow-md dark:from-blue-900 dark:to-blue-800">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-blue-700 dark:text-blue-200">
                      مصرف کل
                    </span>
                    <IoIosWater className="text-xl text-blue-500" />
                  </div>
                  {loading ? (
                    <div className="mt-2 h-5 w-20 animate-pulse rounded bg-blue-300/40" />
                  ) : (
                    <div className="mt-1 flex items-baseline gap-1 text-xl font-extrabold text-blue-800 dark:text-blue-100">
                      <span className="text-xs font-medium">m³</span>
                      <span>
                        {formatAsCubicMeter(
                          summary?.consumption?.total_all_devices
                        )}
                      </span>
                    </div>
                  )}
                </div>

                {/* بیشترین مصرف ماه جاری */}
                <div className="flex flex-1 flex-col justify-between rounded-2xl bg-gradient-to-br from-amber-50 to-amber-100 p-3 shadow-sm transition-all duration-200 hover:shadow-md dark:from-amber-900 dark:to-amber-800">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-amber-700 dark:text-amber-200">
                      بیشترین مصرف ماه جاری
                    </span>
                    <FaFaucetDrip className="text-xl text-amber-500" />
                  </div>
                  {loading ? (
                    <div className="mt-2 h-5 w-24 animate-pulse rounded bg-amber-300/40" />
                  ) : (
                    <>
                      <div className="mt-1 truncate text-sm font-semibold text-amber-900 dark:text-amber-100">
                        {summary?.consumption?.top_month?.name || "__"}
                      </div>
                      <div className="truncate text-xs text-amber-700 dark:text-amber-200">
                        {summary?.consumption?.top_month?.serial || ""}
                      </div>
                      <div className="mt-1 flex items-baseline gap-1 text-base font-bold text-amber-800 dark:text-amber-100">
                        <span className="text-xs font-medium">m³</span>
                        <span>
                          {summary?.consumption?.top_month
                            ? formatAsCubicMeter(
                                summary.consumption.top_month.value
                              )
                            : "__"}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* بیشترین مصرف کل */}
                <div className="flex flex-1 flex-col justify-between rounded-2xl bg-gradient-to-br from-purple-50 to-purple-100 p-3 shadow-sm transition-all duration-200 hover:shadow-md dark:from-purple-900 dark:to-purple-800">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-purple-700 dark:text-purple-200">
                      بیشترین مصرف کل
                    </span>
                    <MdLeaderboard className="text-xl text-purple-500" />
                  </div>
                  {loading ? (
                    <div className="mt-2 h-5 w-24 animate-pulse rounded bg-purple-300/40" />
                  ) : (
                    <>
                      <div className="mt-1 truncate text-sm font-semibold text-purple-900 dark:text-purple-100">
                        {summary?.consumption?.top_all_time?.name || "__"}
                      </div>
                      <div className="truncate text-xs text-purple-700 dark:text-purple-200">
                        {summary?.consumption?.top_all_time?.serial || ""}
                      </div>
                      <div className="mt-1 flex items-baseline gap-1 text-base font-bold text-purple-800 dark:text-purple-100">
                        <span className="text-xs font-medium">m³</span>
                        <span>
                          {summary?.consumption?.top_all_time
                            ? formatAsCubicMeter(
                                summary.consumption.top_all_time.value
                              )
                            : "__"}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* big chart */}
            <div className="overflow-hidden rounded-xl border p-2">
              <div className="text-[12px] font-bold">نمودار مصرف</div>
              <div className="mt-1 min-w-0">
                <ProjectConsumptionChart
                  projectData={projectData}
                  isDashboard={true}
                  selectedMeters={selectedMeters}
                />
              </div>
            </div>
          </div>

          {/* RIGHT (col-span-4 on lg): auto height with max cap to avoid empty gaps */}
          <div className="col-span-1 flex min-w-0 flex-col gap-3 md:col-span-2 lg:col-span-4">
            {/* outdated */}
            <div className="flex max-h-[520px] flex-col overflow-hidden rounded-xl border p-2 sm:max-h-[560px] lg:max-h-[620px]">
              <div className="mr-2 mt-3 flex items-center justify-between">
                <div className="text-[14px] font-bold text-red-500">
                  دستگاه‌های با تأخیر در ارسال (بیش از یک روز)
                </div>
                {!loading && canShowMoreOutdated && (
                  <CustomButton
                    text="نمایش بیشتر"
                    onClick={() => setOutdatedLimit((v) => v + 5)}
                    color="blue"
                    extra="!h-7 !px-3"
                  />
                )}
              </div>
              <div className="overflow-auto">
                <SimpleTable
                  header={outdatedHeader}
                  data={
                    loading
                      ? Array.from({ length: 5 }).map(() =>
                          sk(["name", "serial", "last", "delay", "actions"])
                        )
                      : outdatedData
                  }
                  hasIndex
                  extra="text-xs"
                />
              </div>
              {/* footer */}
              <div className="mt-1 text-[11px] text-gray-600">
                {`${Math.min(outdatedLimit, outdatedFull.length)} از ${
                  outdatedFull.length
                } دستگاه`}
              </div>
            </div>

            {/* recent */}
            <div className="flex max-h-[520px] flex-col overflow-hidden rounded-xl border p-2 sm:max-h-[560px] lg:max-h-[620px]">
              <div className="mr-2 mt-3 flex items-center justify-between">
                <div className="text-[14px] font-bold text-green-500">
                  آخرین ثبت‌های مصرف
                </div>
                {!loading && canShowMoreRecent && (
                  <CustomButton
                    text="نمایش بیشتر"
                    onClick={() => setRecentLimit((v) => v + 5)}
                    color="gray"
                    extra="!h-7 !px-3"
                  />
                )}
              </div>
              <div className="overflow-auto">
                <SimpleTable
                  header={recentHeader}
                  data={
                    loading
                      ? Array.from({ length: 5 }).map(() => ({
                          name: (
                            <div className="h-3 w-20 animate-pulse rounded bg-gray-300 dark:bg-gray-700" />
                          ),
                          serial: (
                            <div className="h-3 w-16 animate-pulse rounded bg-gray-300 dark:bg-gray-700" />
                          ),
                          time: (
                            <div className="h-3 w-28 animate-pulse rounded bg-gray-300 dark:bg-gray-700" />
                          ),
                          val: (
                            <div className="h-3 w-12 animate-pulse rounded bg-gray-300 dark:bg-gray-700" />
                          ),
                          cum: (
                            <div className="h-3 w-12 animate-pulse rounded bg-gray-300 dark:bg-gray-700" />
                          ),
                        }))
                      : recentData
                  }
                  hasIndex
                  extra="text-xs"
                />
              </div>
              {/* footer */}
              <div className="mt-1 text-[11px] text-gray-600">
                {`${Math.min(recentLimit, recentFull.length)} از ${
                  recentFull.length
                } دستگاه`}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <WaterMetersPicker
        isOpen={metersOpen}
        onClose={() => setMetersOpen(false)}
        options={meterOptions}
        value={selectedMeters}
        onChange={setSelectedMeters}
        onApply={() => {
          setMetersOpen(false);
          refetch(); // recompute with chosen meters
        }}
      />
    </div>
  );
};

export default ProjectAbstract;
